import { createContext, useContext, useState, useReducer, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useLocation } from 'react-router-dom';

const initialState = null;
const LastLocationContext = createContext(null);

const useLastLocation = () => {
    return Object.assign({}, useContext(LastLocationContext));
};

const actions = {
    SET_LAST_LOCATION: 'SET_LAST_LOCATION'
};

const reducer = (state, action) => {
    switch (action.type) {
        case actions.SET_LAST_LOCATION:
            return action.payload;
        default:
            return state;
    }
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * I could only check `key` here but according to:
 * @see https://reacttraining.com/react-router/web/api/location
 * `key` property is not available when HashHistory is used.
 */
const props = ['key', 'pathname', 'search', 'hash'];
const isEqual = (a, b) => props.every((prop) => a[prop] === b[prop]);
const prevented = [];
const prevent = (location) => {
    const rest = __rest(location, ["state"]);
    prevented.push(rest);
};
const hasBeenPrevented = (location) => prevented.some((preventedLocation) => isEqual(location, preventedLocation));
const shouldPrevent = (location) => { var _a; return Boolean((_a = location.state) === null || _a === void 0 ? void 0 : _a.preventLastLocation); };

const LastLocationProvider = ({ children, watchOnlyPathname = false }) => {
    const [currentLocation, setCurrentLocation] = useState(null);
    const location = useLocation();
    const [state, dispatch] = useReducer(reducer, initialState);
    const value = {
        lastLocation: state,
        setLastLocation: (location) => {
            dispatch({ type: actions.SET_LAST_LOCATION, payload: location });
        }
    };
    const updateLastLocation = () => {
        if (location === null || location === currentLocation || (watchOnlyPathname && (currentLocation === null || currentLocation === void 0 ? void 0 : currentLocation.pathname) === location.pathname)) {
            return;
        }
        if (shouldPrevent(location) && !hasBeenPrevented(location)) {
            prevent(location);
            return;
        }
        value.setLastLocation(currentLocation);
    };
    useEffect(() => {
        updateLastLocation();
        setCurrentLocation(location);
    }, [location]);
    return (jsx(LastLocationContext.Provider, Object.assign({ value: value }, { children: children })));
};

function withLastLocation(WrappedComponent) {
    const WithLocationComponent = (props) => {
        const lastLocation = useLastLocation();
        return jsx(WrappedComponent, Object.assign({}, props, { lastLocation: lastLocation }));
    };
    return WithLocationComponent;
}

export { LastLocationContext, LastLocationProvider, initialState, useLastLocation, withLastLocation };
//# sourceMappingURL=index.es.js.map
